import { useLayoutEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/Button"
import { join } from "@/utils/join"

import { useNotional } from "../../NotionalInput"
import { useTileCurrencyPair } from "../../TileContext"

interface ProgressBarProps {
  width: number
  transitionDuration: number
}

const ProgressBar = ({ width, transitionDuration }: ProgressBarProps) => (
  <div className="h-full w-full rounded-xs bg-tertiary">
    <div
      style={{
        // tailwindcss does not support dynamic styles
        width: `${width}%`,
        transitionDuration: `${transitionDuration}ms`,
      }}
      className={join(
        "linear h-full rounded-xs bg-brand-primary transition-[width] ease-linear",
      )}
    />
  </div>
)

const TimeProgress = ({ start, end }: { start: number; end: number }) => {
  const [state, setState] = useState({
    transitionDuration: 0,
    width: ((end - Date.now()) / (end - start)) * 100,
    end,
  })

  useLayoutEffect(() => {
    if (state.transitionDuration > 0 && state.width === 0) return
    const token = requestAnimationFrame(() => {
      setState((prev) => {
        return {
          ...prev,
          ...(prev.transitionDuration > 0
            ? { width: 0 }
            : { transitionDuration: prev.end - Date.now() }),
        }
      })
    })
    return () => {
      cancelAnimationFrame(token)
    }
  }, [state])

  return <ProgressBar {...state} />
}

const SecsTimer = ({
  start,
  end,
  isStatic,
}: {
  start: number
  end: number
  isStatic?: boolean
}) => {
  const { t } = useTranslation()

  const [timeLeftSecs, setTimeLeftSecs] = useState(() =>
    Math.round((end - Date.now()) / 1000),
  )
  useLayoutEffect(() => {
    if (timeLeftSecs === 0) return
    const token = setTimeout(
      () => setTimeLeftSecs((x) => x - 1),
      end - (timeLeftSecs - 1) * 1000 - Date.now(),
    )
    return () => clearTimeout(token)
  }, [timeLeftSecs, end])

  return (
    <p className="text-xs-regular text-secondary tabular-nums">
      {isStatic ? (
        <>{end - start} secs</>
      ) : (
        <>
          {timeLeftSecs} {t("liveRates.rfq.timerSecsAbbr")}
        </>
      )}
    </p>
  )
}

interface TimerProps {
  start: number
  end: number
  isStatic?: boolean
  staticProgressWidth?: number
}

const Timer = ({ staticProgressWidth, ...props }: TimerProps) => (
  <>
    <div className="mr-xs whitespace-nowrap">
      <SecsTimer {...props} />
    </div>
    <div className="h-sm w-full">
      {staticProgressWidth ? (
        <ProgressBar width={staticProgressWidth} transitionDuration={0} />
      ) : (
        <TimeProgress {...props} />
      )}
    </div>
  </>
)

export interface RfqTimerProps extends TimerProps {
  onReject: () => void
}

export const RfqTimer = ({ onReject, ...props }: RfqTimerProps) => {
  const { t } = useTranslation()
  const [, notional] = useNotional()
  const { base } = useTileCurrencyPair()
  return (
    <div className="flex h-full w-full items-center justify-start gap-xs px-sm">
      <div className="border-r-3 border-secondary pr-sm">
        <p className="inline pr-xs text-sm-regular text-tertiary">{base}</p>
        <p className="inline text-sm-regular text-primary">{notional}</p>
      </div>
      <div
        className="flex flex-1 items-center gap-sm self-stretch"
        data-testid="rfqTimer"
      >
        <Timer {...props} />
        <Button
          variant="primary"
          size="xs"
          data-testid="rfqReject"
          onClick={onReject}
        >
          {t("liveRates.rfq.reject")}
        </Button>
      </div>
    </div>
  )
}
